import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function AdminLogin() {
  const { t } = useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const result = await login(email, password);
    setSubmitting(false);
    if (result.ok) navigate("/admin");
    else setError(result.error || t("admin.login.invalidCredentials"));
  };

  const handleForgotPassword = async () => {
    setResetSubmitting(true);
    setResetMessage("");
    try {
      const { data } = await api.post("/auth/forgot-password");
      setResetMessage(data.message);
    } catch (err) {
      setResetMessage(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm bg-[#141414] border border-white/10 rounded-xl p-8">
        <h1 className="font-serif text-2xl text-white mb-1">
          Elite<span className="text-gold">The</span>Barber
        </h1>
        <p className="text-white/40 text-sm mb-8">{t("admin.login.title")}</p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="admin-email" className="text-white/70 text-sm">{t("admin.login.email")}</Label>
            <Input id="admin-email" type="email" data-testid="admin-login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white focus:border-gold rounded-none mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-white/70 text-sm">{t("admin.login.password")}</Label>
            <Input id="admin-password" type="password" data-testid="admin-login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white focus:border-gold rounded-none mt-1.5" required />
          </div>
        </div>

        {error && <p data-testid="admin-login-error" className="text-red-400 text-sm mt-4">{error}</p>}

        <Button type="submit" data-testid="admin-login-submit-button" disabled={submitting} className="w-full mt-6 bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-60">
          {submitting ? t("common.loading") : t("admin.login.submit")}
        </Button>

        {resetMessage ? (
          <p data-testid="admin-login-reset-message" className="text-center mt-4 text-white/60 text-sm">{resetMessage}</p>
        ) : (
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetSubmitting}
            data-testid="admin-login-forgot-password-button"
            className="block w-full text-center mt-4 text-white/50 text-sm hover:text-white/80 disabled:opacity-50"
          >
            {resetSubmitting ? t("common.loading") : t("admin.login.forgotPassword")}
          </button>
        )}
      </form>
    </div>
  );
}
